import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SitesService } from '../sites.service';

export interface SiteFormDialogData {
  siteId?: string;
}

@Component({
  selector: 'app-site-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>store</mat-icon>
        {{ isEditMode ? 'Edit Site' : 'Create New Site' }}
      </h2>
      <button mat-icon-button class="close-button" type="button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="siteForm" (ngSubmit)="onSubmit()" id="siteForm">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Site Name</mat-label>
            <input matInput formControlName="name" placeholder="Enter site name" required />
            <mat-icon matPrefix>store</mat-icon>
            <mat-error *ngIf="siteForm.get('name')?.invalid">
              {{ getErrorMessage('name') }}
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Description</mat-label>
            <input matInput formControlName="description" placeholder="Enter site description (optional)" />
            <mat-icon matPrefix>description</mat-icon>
            <mat-hint>Brief description of the site</mat-hint>
            <mat-error *ngIf="siteForm.get('description')?.invalid">
              {{ getErrorMessage('description') }}
            </mat-error>
          </mat-form-field>
        </div>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">
            <strong>Active Site</strong>
            <small>Enable or disable this site</small>
          </mat-slide-toggle>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="siteForm" type="submit"
        [disabled]="loading || siteForm.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }} Site</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      min-width: 560px;
      padding-top: 8px;
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-row mat-form-field {
      flex: 1;
    }

    .full-width {
      width: 100%;
    }

    .form-section {
      margin: 16px 0;
    }

    .form-section mat-slide-toggle {
      display: flex;
      flex-direction: column;
    }

    .form-section small {
      color: #666;
      font-size: 12px;
      margin-top: 2px;
    }

    mat-dialog-actions button mat-spinner {
      display: inline-block;
      margin-right: 8px;
    }

    @media (max-width: 640px) {
      mat-dialog-content {
        min-width: unset;
      }

      .form-row {
        flex-direction: column;
        gap: 0;
      }
    }
  `],
})
export class SiteFormDialogComponent {
  siteForm: FormGroup;
  loading = false;
  isEditMode: boolean;
  siteId: string | undefined;

  constructor(
    private fb: FormBuilder,
    private sitesService: SitesService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<SiteFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SiteFormDialogData,
  ) {
    this.siteId = data?.siteId;
    this.isEditMode = !!this.siteId;

    this.siteForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(500)]],
      is_active: [true],
    });

    if (this.isEditMode) {
      this.loadSite();
    }
  }

  loadSite(): void {
    if (!this.siteId) return;
    this.loading = true;
    this.sitesService.getSite(this.siteId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const site = response.data;
          this.siteForm.patchValue({
            name: site.name,
            description: site.description,
            is_active: site.is_active,
          });
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load site details', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onSubmit(): void {
    if (this.siteForm.invalid) {
      this.siteForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = {
      ...this.siteForm.value,
    };

    const request$ = this.isEditMode && this.siteId
      ? this.sitesService.updateSite(this.siteId, formValue)
      : this.sitesService.createSite(formValue);

    request$.subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open(
            `Site ${this.isEditMode ? 'updated' : 'created'} successfully`,
            'Close',
            { duration: 3000 },
          );
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        const message =
          error.error?.message ||
          `Failed to ${this.isEditMode ? 'update' : 'create'} site`;
        this.snackBar.open(message, 'Close', { duration: 5000 });

        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach((key) => {
            const control = this.siteForm.get(key);
            if (control) {
              control.setErrors({ serverError: error.error.errors[key][0] });
            }
          });
        }

        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getErrorMessage(controlName: string): string {
    const control = this.siteForm.get(controlName);
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return 'This field is required';
    }
    if (control.errors['maxlength']) {
      return `Maximum length is ${control.errors['maxlength'].requiredLength} characters`;
    }
    if (control.errors['serverError']) {
      return control.errors['serverError'];
    }

    return 'Invalid value';
  }
}
